import React from 'react'
import clsx from 'clsx'
import { useForm, FormProvider } from 'react-hook-form'

import { RatingContext } from '../utils/RatingContext'
import { checkConfigIdService, sendFeedbackService, ApiError } from '../services/RatingService'
import { PSpinner, PText } from '@porsche-design-system/components-react'
import styles from '../assets/styles/styles.module.css'
import { TEXT } from '../i18n/text'
import { RatingTitle } from './Rating/RatingTitle'
import { ButtonBar } from './Rating/ButtonBar'
import { RatingGuided } from './Rating/RatingGuided'
import { RatingDefault } from './Rating/RatingDefault'
import { RatingError } from './Rating/RatingError'
import { RatingThankyou } from './Rating/RatingThankYou'
import { isValidRating } from '../utils/ratingValidation'
import { isValidFeedback, normalizeFeedback } from '../utils/feedbackValidation'

export const RatingSection = (): React.ReactElement => {

    const configId = React.useContext(RatingContext).configId;
    const apiKey = React.useContext(RatingContext).apiKey;
    const setFormError = React.useContext(RatingContext).setFormError;
    const setPreCheck = React.useContext(RatingContext).setPreCheck;
    const setLoading = React.useContext(RatingContext).setLoading;
    const preCheck = React.useContext(RatingContext).preCheck;
    const variant = React.useContext(RatingContext).variant;
    const isTest = React.useContext(RatingContext).isTest;
    const formError = React.useContext(RatingContext).formError;
    const language = React.useContext(RatingContext).language;
    const requestError = React.useContext(RatingContext).requestError;
    const setRequestError = React.useContext(RatingContext).setRequestError;
    const formCompleted = React.useContext(RatingContext).formCompleted;
    const setFormCompleted = React.useContext(RatingContext).setFormCompleted;
    const rating = React.useContext(RatingContext).rating;
    const appName = React.useContext(RatingContext).appName;
    const setErrorCode = React.useContext(RatingContext).setErrorCode;
    const methods = useForm<Rating.FormValues>()

    const submitFeedback = (data: Rating.FormValues) => {
        const comment = normalizeFeedback(data.comment);
        if (!isValidFeedback(comment)) {
            methods.setError("comment", { type: "validate", message: TEXT[language].feedbackErrorTooLong });
            return;
        }
        if (!isValidRating(rating)) {
            return setFormError(true);
        }
        setFormError(false)
        setLoading(true)
        sendFeedbackService({
            comment: comment,
            rating: rating,
            configId: configId,
            test: isTest || false
        }, apiKey)
            .then(() => {
                setFormCompleted(true)
            })
            .catch(() => {
                setRequestError(true)
                setFormCompleted(true)
            })
    }

    React.useEffect(() => {
        if (!configId) return

        checkConfigIdService(configId)
            .then(() => {
                setPreCheck(false)
                setErrorCode('200')
            })
            .catch((error: unknown) => {
                setPreCheck(false)
                const isConfigNotFound = error instanceof ApiError && error.status === 404
                setErrorCode(isConfigNotFound ? '302' : '303')
                setRequestError(true)
                setFormCompleted(true)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configId])

    return (
        <main className={styles.section}>
            <div className={'pag-rating__container'}>
                {preCheck && <PSpinner className={styles.spinner} size='medium' />}
                <FormProvider {...methods}>
                    <form
                        className={clsx(styles.form, { [styles.formInvisible]: preCheck, [styles.formGuided]: variant === 'guided' })}
                        onSubmit={methods.handleSubmit(submitFeedback)}>
                        <RatingTitle appName={appName} />
                        {!formCompleted && !requestError && (
                            <React.Fragment>
                                {variant === 'guided' && <RatingGuided />}
                                {variant !== 'guided' && <RatingDefault />}
                            </React.Fragment>
                        )}
                        {requestError && <RatingError />}
                        {formCompleted && !requestError && <RatingThankyou />}
                        <ButtonBar />
                        {formError && (
                            <PText color='brand' className={styles.error}>
                                {TEXT[language].errorMessage}
                            </PText>
                        )}
                    </form>
                </FormProvider>
            </div>
        </main>
    )
}
